import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Award, Clock, BookOpen, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

/* Category → gradient (used by the default thumbnail when a course has no
 * uploaded image). On-brand: black base + gold accent slash per category. */
const CATEGORY_THEME = {
  "Royalty Infrastructure": {
    bg: "linear-gradient(135deg, #1a1207 0%, #0a0a0a 55%, #2a1f08 100%)",
    accent: "#D4AF37",
    glyph: "₪", // royalty/currency vibe
  },
  "Rights Protection": {
    bg: "linear-gradient(135deg, #1a0b0b 0%, #0a0a0a 55%, #2a0f0f 100%)",
    accent: "#E11D48",
    glyph: "◈",
  },
  "Metadata Authority": {
    bg: "linear-gradient(135deg, #0b1a18 0%, #0a0a0a 55%, #082019 100%)",
    accent: "#10B981",
    glyph: "◉",
  },
  "Catalog Operations": {
    bg: "linear-gradient(135deg, #0b1322 0%, #0a0a0a 55%, #07172b 100%)",
    accent: "#38BDF8",
    glyph: "◫",
  },
  "Business Structure": {
    bg: "linear-gradient(135deg, #160b1f 0%, #0a0a0a 55%, #1a0a26 100%)",
    accent: "#A78BFA",
    glyph: "▣",
  },
  Infrastructure: {
    bg: "linear-gradient(135deg, #141414 0%, #0a0a0a 55%, #1a1a1a 100%)",
    accent: "#D4AF37",
    glyph: "◆",
  },
};

function getTheme(category) {
  return CATEGORY_THEME[category] || CATEGORY_THEME.Infrastructure;
}

/* Derive access badge. Backend may set `access_tier` ("free"|"founding"|
 * "premium") — if absent, fall back to inferring from score_boost. */
function getAccess(course) {
  const tier = (course.access_tier || "").toLowerCase();
  if (tier === "premium") return { label: "Premium", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  if (tier === "founding") return { label: "Founding Producer", className: "bg-rose-500/10 text-rose-300 border-rose-500/30" };
  if (tier === "free") return { label: "Free", className: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
  // Auto-derive
  const boost = course.score_boost || 0;
  if (boost >= 40) return { label: "Premium", className: "bg-amber-500/15 text-amber-400 border-amber-500/30" };
  if (boost >= 25) return { label: "Founding Producer", className: "bg-rose-500/10 text-rose-300 border-rose-500/30" };
  return { label: "Free", className: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" };
}

function CourseThumbnail({ course }) {
  const theme = getTheme(course.category);
  if (course.thumbnail_url) {
    return (
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-black">
        <img
          src={course.thumbnail_url}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      </div>
    );
  }
  // Brand-styled default thumbnail
  return (
    <div
      className="relative aspect-[16/10] w-full overflow-hidden"
      style={{ background: theme.bg }}
    >
      {/* gold mark watermark */}
      <img
        src="/beatcredsi-mark.png"
        alt=""
        aria-hidden
        className="absolute -right-6 -bottom-6 w-44 h-44 opacity-25 group-hover:opacity-40 transition-opacity duration-500"
      />
      {/* faint grain */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
          backgroundSize: "3px 3px",
        }}
      />
      {/* accent bar */}
      <div
        className="absolute top-0 left-0 h-1 w-24"
        style={{ background: theme.accent }}
      />
      {/* category label + glyph */}
      <div className="absolute inset-0 p-5 flex flex-col justify-between">
        <div
          className="font-mono-tech text-xs uppercase tracking-[0.2em]"
          style={{ color: theme.accent }}
        >
          {course.category}
        </div>
        <div className="font-display text-2xl lg:text-3xl font-medium leading-tight text-white max-w-[80%]">
          {course.title}
        </div>
      </div>
      <div
        className="absolute top-4 right-4 font-display text-4xl opacity-30"
        style={{ color: theme.accent }}
      >
        {theme.glyph}
      </div>
    </div>
  );
}

function CourseCard({ course }) {
  const done = course.progress?.exam_passed;
  const lessonsDone = course.progress?.lessons_completed || 0;
  const lessonsTotal = course.progress?.lessons_total || (course.lessons?.length || 0);
  const access = getAccess(course);

  return (
    <Link
      to={`/courses/${course.slug}`}
      data-testid={`course-card-${course.slug}`}
      className="group flex flex-col bg-[#0c0c0c] border border-white/5 hover:border-amber-500/40 transition-all duration-300 hover:shadow-[0_0_40px_-12px_rgba(212,175,55,0.35)] overflow-hidden"
    >
      <CourseThumbnail course={course} />

      <div className="flex flex-col flex-1 p-5">
        {/* Top row: access badge + cert status */}
        <div className="flex items-center justify-between mb-3 gap-2">
          <span
            className={`text-xs font-mono-tech uppercase tracking-[0.15em] px-2 py-1 border ${access.className}`}
          >
            {access.label}
          </span>
          {done ? (
            <span className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400 flex items-center gap-1">
              <CheckCircle2 size={12} /> Certified
            </span>
          ) : (
            <span className="text-xs font-mono-tech uppercase tracking-widest text-amber-500 flex items-center gap-1">
              <Sparkles size={12} /> +{course.score_boost}
            </span>
          )}
        </div>

        {/* Title */}
        <h3
          className="font-display text-lg lg:text-xl font-medium leading-tight mb-2 text-white group-hover:text-amber-400 transition-colors"
          data-testid={`course-title-${course.slug}`}
        >
          {course.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-neutral-400 leading-relaxed mb-4 line-clamp-3">
          {course.summary}
        </p>

        {/* Meta row */}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] font-mono-tech uppercase tracking-widest text-neutral-500 mb-4">
          <span className="flex items-center gap-1.5">
            <Clock size={11} /> ~{course.estimated_minutes}min
          </span>
          <span className="flex items-center gap-1.5">
            <BookOpen size={11} /> {lessonsDone}/{lessonsTotal} lessons
          </span>
          <span className="flex items-center gap-1.5">
            <Award size={11} /> {course.certification_name}
          </span>
        </div>

        {/* Reason chip */}
        {course.reason && (
          <div className="text-[13px] text-amber-500/80 font-mono-tech mb-3 line-clamp-1">
            ↳ {course.reason}
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          tabIndex={-1}
          data-testid={`start-course-${course.slug}`}
          className="w-full flex items-center justify-center gap-2 py-3 bg-white/[0.04] border border-white/10 text-sm font-mono-tech uppercase tracking-[0.15em] text-neutral-200 group-hover:bg-amber-500 group-hover:text-black group-hover:border-amber-500 transition-all duration-300"
        >
          {done ? (
            <>Review Course <ArrowRight size={14} /></>
          ) : lessonsDone > 0 ? (
            <>Continue Course <ArrowRight size={14} /></>
          ) : (
            <>Start Course <ArrowRight size={14} /></>
          )}
        </button>
      </div>
    </Link>
  );
}

export default function Courses() {
  const [data, setData] = useState({ courses: [], score: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/learning/courses");
        setData(r.data);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="text-overline mb-3">
          BeatCredsi Academy<span className="text-amber-500">™</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-3">
          Learn. Certify. Improve.
        </h1>
        <div className="font-mono-tech text-xs text-amber-500 uppercase tracking-widest mb-3">
          Infrastructure Over Hype<span className="text-amber-500/70">™</span>
        </div>
        <p className="text-sm text-neutral-400 mb-12 max-w-2xl">
          Courses recommended based on your BeatCredsi Score, missing infrastructure, and current tier. Pass the exam (80% to certify) — your score climbs and a badge lands on your Passport.
        </p>

        {loading ? (
          <div className="text-sm text-neutral-500 font-mono-tech" data-testid="courses-loading">
            Loading roadmap…
          </div>
        ) : data.courses.length === 0 ? (
          <div className="border border-white/5 p-8 text-sm text-neutral-500" data-testid="courses-empty">
            Take the <Link to="/assessment" className="text-amber-500 hover:underline">assessment</Link> first to unlock your personalized roadmap.
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="courses-grid"
          >
            {data.courses.map((c) => (
              <CourseCard key={c.id} course={c} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
